import { ButtonDropdownProps } from '@amzn/awsui-components-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

// context
import { useUser } from '@/src/store/user.context';

// utils
import { getLanguageCodeSafe, getLocaleLabel, LANGUAGE_SELECT_OPTIONS } from '@/src/utils/locale.utils';
import { preProdLogger } from '@/src/utils/log.utils';
import { getNavMenuForUser } from '@/src/utils/navMenu.utils';

// ui components
import { Header } from '@/src/components/ui/organisms/Header/Header';

// types
import { useActiveRouteDetails } from '@/src/hooks/useActiveRouteDetails';
import { useApi } from '@/src/store/api.context';
import type { CancelableEventHandler } from '@amzn/awsui-components-react/polaris/internal/events';

const MANUAL_LANGUAGE_SELECTION_KEY = 'hasManuallySelectedLanguage';

export const NewHeader: React.FC = () => {
  const { i18n } = useTranslation();
  const history = useHistory();
  const [selectedLanguage, setSelectedLanguage] = useState(getLanguageCodeSafe(i18n.language));
  const [hasManuallySelectedLanguage, setHasManuallySelectedLanguage] = useState(() => {
    // Initialize from localStorage to persist across page refreshes
    return localStorage.getItem(MANUAL_LANGUAGE_SELECTION_KEY) === 'true';
  });
  const { user } = useUser();
  const { isCreateJamProfile } = useActiveRouteDetails();
  const { userApi } = useApi();

  useEffect(() => {
    const preferredLanguageCode = user?.preferredLanguageCode;

    // Only apply profile language if user hasn't manually selected a language in this session
    if (!preferredLanguageCode || hasManuallySelectedLanguage) {
      return;
    }

    handleLanguageSelect(preferredLanguageCode);
  }, [user, hasManuallySelectedLanguage]);

  const handleLanguageSelect = useCallback(
    (code: string) => {
      void i18n.changeLanguage(code);
      setSelectedLanguage(code);
    },
    [i18n]
  );

  const langOptions = useMemo(() => {
    return {
      lang: {
        value: selectedLanguage,
        label: getLocaleLabel(selectedLanguage),
      },
      options: LANGUAGE_SELECT_OPTIONS,
      onLanguageSelect: (code: string) => {
        // Mark that user has manually selected a language and persist to localStorage
        setHasManuallySelectedLanguage(true);
        localStorage.setItem(MANUAL_LANGUAGE_SELECTION_KEY, 'true');
        userApi
          .changePreferrendLanguageCode(code)
          .then(() => {
            handleLanguageSelect(code);
            history.go(0);
          })
          .catch((err) => preProdLogger(`Error changing user language preference`, err));
      },
    };
  }, [selectedLanguage, handleLanguageSelect]);

  const handleMenuItemClick: CancelableEventHandler<ButtonDropdownProps.ItemClickDetails | object> = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const detail = e.detail as ButtonDropdownProps.ItemClickDetails;
      if (detail && detail?.href) {
        history.push(detail?.href);
      }
    },
    []
  );

  const navMenu = useMemo(() => {
    return getNavMenuForUser(user);
  }, [user]);

  return (
    <Header navItems={isCreateJamProfile ? [] : navMenu} lang={langOptions} onMenuItemClick={handleMenuItemClick} />
  );
};
